import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { UseQueryResult } from "@tanstack/react-query";
import { PropsWithChildren, useState } from "react";
import RecordViewContext from "./RecordContext";
import RecordField from "./RecordField";
import { Alert, AlertTitle, AlertDescription } from "../ui/alert";
import { useTranslation } from "react-i18next";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface RecordViewProps<T extends Record<string, unknown>>
  extends PropsWithChildren {
  query: UseQueryResult<T>;
  translationKey: string;
  header?: (data: T) => React.ReactNode;
  actions?: React.ReactNode[];
  isOpen?: boolean;
  className?: string;
}

const RecordView = <T extends Record<string, unknown>>({
  children,
  translationKey,
  query,
  actions = [],
  header,
  className,
  isOpen: isOpenFromProps = false,
}: RecordViewProps<T>) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(isOpenFromProps);

  if (query.isError) {
    return (
      <Card className={className}>
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <AlertTitle>{t("errors.genericTitle")}</AlertTitle>
            <AlertDescription>{t("errors.unknownError")}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      data-testid={`${translationKey}-collapsible`}
    >
      <Card className={className}>
        <RecordViewContext.Provider value={{ translationKey, query }}>
          <CardHeader className="flex flex-row justify-between items-center space-y-0 gap-4 p-2">
            {query.isLoading ? (
              <Skeleton className="w-full h-8" />
            ) : (
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="p-0 pl-2 w-full flex items-center justify-start gap-2"
                >
                  <ChevronDown
                    className={cn(
                      "transition-transform duration-200 shrink-0",
                      isOpen ? "rotate-180" : "rotate-0",
                    )}
                  />
                  <CardTitle className="text-base">
                    {query.data && header ? header(query.data) : null}
                  </CardTitle>
                </Button>
              </CollapsibleTrigger>
            )}
            <div className="flex gap-2">{actions}</div>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 border-t pt-5">
              {children}
            </CardContent>
          </CollapsibleContent>
        </RecordViewContext.Provider>
      </Card>
    </Collapsible>
  );
};

export default RecordView;

RecordView.Field = RecordField;
